import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  campaignsQueryFrom,
  createCampaign,
  deleteCampaign,
  discoverCampaigns,
  fetchCampaign,
  listCampaigns,
  updateCampaign,
} from '@/api/campaigns';
import { queryKeys } from '@/lib/query-keys';
import type {
  CampaignFiltros,
  CreateCampaignRequest,
  UpdateCampaignRequest,
} from '@/types/campaigns';

/**
 * Gasto por anuncio/campaña del negocio activo (módulo 10).
 *
 * Leerlo lo puede hacer cualquier miembro (los reportes usan el mismo dato); crear, editar, borrar
 * y «descubrir» son solo ADMIN (`requireRole('ADMIN')`, 403 si alguien invoca el endpoint a mano).
 *
 * Cuatro detalles del contrato que se ven acá:
 *  - el listado tiene filtros reales (`activo`, `buscar`, `metaAdId`, `metaCampaignId`), así que van
 *    a la query key: cambiar de filtro es otra entrada de caché y no una lista mal filtrada, con
 *    `keepPreviousData` para que la tabla no parpadee;
 *  - las escrituras invalidan el catálogo COMPLETO (`queryKeys.campaigns.all`): crear, editar,
 *    borrar y descubrir cambian `total` y `gastoTotal`, que el backend calcula por su cuenta;
 *  - «descubrir» además SIEMBRA la caché del listado sin filtros con los `items` que devuelve (y el
 *    `gastoTotal` sumado de esos items, que es el catálogo entero porque el endpoint no pagina):
 *    el admin ve las filas nuevas sin esperar un segundo viaje;
 *  - el módulo no toca conversaciones ni órdenes, así que no invalida nada fuera de sí mismo. OJO:
 *    cuando exista la pantalla de Reportes (paso 8), sus claves (`queryKeys.reports.*`) tendrán que
 *    invalidarse también desde acá, porque el CPA se calcula con este gasto.
 */

/** `GET /api/tenants/:tenantId/campaigns` — filas + `total` + `gastoTotal` del filtro aplicado. */
export function useCampaigns(tenantId: string | null, filters: CampaignFiltros = {}) {
  // Los filtros que viajan al backend son los mismos que forman la query key (como en Órdenes).
  const query = campaignsQueryFrom(filters);

  return useQuery({
    queryKey: queryKeys.campaigns.list(tenantId ?? '', query),
    queryFn: () => listCampaigns(tenantId ?? '', filters),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/**
 * `GET /api/tenants/:tenantId/campaigns/:campaignId` — una fila de gasto.
 *
 * La tabla no lo necesita (el listado trae la fila entera), así que la pantalla no lo usa hoy:
 * queda para el detalle de una fila y para que el smoke compruebe el aislamiento entre negocios.
 * Con `campaignId` vacío la query no corre.
 */
export function useCampaign(tenantId: string | null, campaignId: string | null) {
  return useQuery({
    queryKey: queryKeys.campaigns.detail(tenantId ?? '', campaignId ?? ''),
    queryFn: () => fetchCampaign(tenantId ?? '', campaignId ?? ''),
    enabled: Boolean(tenantId && campaignId),
  });
}

function invalidateCampaigns(queryClient: QueryClient, tenantId: string | null): void {
  if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all(tenantId) });
}

/** `POST /api/tenants/:tenantId/campaigns` (solo ADMIN) — responde 201 con la fila creada. */
export function useCreateCampaign(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCampaignRequest) => createCampaign(tenantId ?? '', payload),
    onSuccess: () => invalidateCampaigns(queryClient, tenantId),
  });
}

/** `PATCH /api/tenants/:tenantId/campaigns/:campaignId` (solo ADMIN). */
export function useUpdateCampaign(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, payload }: { campaignId: string; payload: UpdateCampaignRequest }) =>
      updateCampaign(tenantId ?? '', campaignId, payload),
    onSuccess: (campaign) => {
      invalidateCampaigns(queryClient, tenantId);
      if (tenantId) {
        queryClient.setQueryData(queryKeys.campaigns.detail(tenantId, campaign.id), campaign);
      }
    },
  });
}

/** `DELETE /api/tenants/:tenantId/campaigns/:campaignId` (solo ADMIN). */
export function useDeleteCampaign(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) => deleteCampaign(tenantId ?? '', campaignId),
    onSuccess: (_result, campaignId) => {
      invalidateCampaigns(queryClient, tenantId);
      if (tenantId) {
        queryClient.removeQueries({ queryKey: queryKeys.campaigns.detail(tenantId, campaignId) });
      }
    },
  });
}

/**
 * `POST /api/tenants/:tenantId/campaigns/descubrir` (solo ADMIN) — idempotente.
 *
 * Crea con gasto 0 las filas de los anuncios/campañas que ya aparecieron en las conversaciones. La
 * respuesta trae el catálogo completo, así que se escribe en la caché del listado sin filtros (el
 * `gastoTotal` se suma de esos `items`: el endpoint no pagina, son todas las filas) y se invalida
 * el módulo para que las combinaciones con filtro se vuelvan a pedir.
 */
export function useDiscoverCampaigns(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => discoverCampaigns(tenantId ?? ''),
    onSuccess: (result) => {
      if (tenantId) {
        const gastoTotal = result.items.reduce((suma, item) => suma + item.gastoActual, 0);

        queryClient.setQueryData(queryKeys.campaigns.list(tenantId, {}), {
          items: result.items,
          total: result.items.length,
          gastoTotal,
        });
      }

      invalidateCampaigns(queryClient, tenantId);
    },
  });
}
